import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { rpc } from "./rpc";
import type { Revision } from "./types";

/** `docs/spec/revisions.md` §4. The list omits the body; `info` carries it. */

export const revisionQueryKeys = {
  list: (documentId: string) => ["revisions.list", documentId] as const,
  info: (id: string) => ["revisions.info", id] as const,
};

export function useRevisions(
  documentId: string | undefined,
  enabled = true,
): UseQueryResult<Revision[]> {
  return useQuery({
    queryKey: revisionQueryKeys.list(documentId ?? ""),
    enabled: enabled && documentId !== undefined && documentId !== "",
    retry: false,
    queryFn: async () => (await rpc<Revision[]>("revisions.list", { documentId, limit: 100 })).data,
  });
}

export function useRevision(id: string | undefined): UseQueryResult<Revision> {
  return useQuery({
    queryKey: revisionQueryKeys.info(id ?? ""),
    enabled: id !== undefined && id !== "",
    retry: false,
    queryFn: async () => (await rpc<Revision>("revisions.info", { id })).data,
  });
}

export function useRenameRevision(
  documentId: string,
): UseMutationResult<Revision, Error, { id: string; name: string | null }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string | null }) =>
      (await rpc<Revision>("revisions.update", input)).data,
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: revisionQueryKeys.list(documentId) });
      void client.invalidateQueries({ queryKey: revisionQueryKeys.info(input.id) });
    },
  });
}
